import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {QueryStatus} from '@reduxjs/toolkit/query/react';
import type {OverviewTaskType} from 'services/settings/types';
import type {OverviewTask, TaskTypeAll} from 'services/tasks/types';
import {Configuration} from 'configuration';
import {useLocalization} from 'localization';
import {useGetLocksQuery} from 'services/locks/locksApi';
import {
  overviewSelectedTaskTypeUpdated,
  selectSettingsOverviewSearchText,
  selectSettingsOverviewSelectedTaskType,
} from 'services/settings';
import {getTaskTypes} from 'services/settings/functions/getTaskTypes';
import {mapTasksToOverviewTasks} from 'services/tasks';
import {useGetTasksQuery} from 'services/tasks/tasksApi';
import {useGetUsersQuery} from 'services/users/usersApi';
import {useAppDispatch, useAppSelector} from 'store/hooks';
import {useColumnConfigState} from 'schema/table/hooks/useColumnConfigState';
import {useSearchedTasks} from './useSearchedTasks';
import {useSetTaskSearchText} from './useSetTaskSearchText';

const LOCKS_RELOAD_FALLBACK_MS = 1000 * 5;
const LOCKS_RELOAD_MS = Configuration.interval.lock ?? LOCKS_RELOAD_FALLBACK_MS;
const LOCKS_DELAY_MS = 400;

export const useOverviewTasks = (): {
  allTasks: OverviewTask[];
  filteredTasks: OverviewTask[];
  taskTypes: OverviewTaskType[];
  selectedTaskType: TaskTypeAll;
  setSelectedTaskType: (taskType: TaskTypeAll) => void;
  searchText: string;
  setSearchText: (searchText: string) => void;
  loading: boolean;
  errorMsg?: string;
} => {
  const localization = useLocalization();

  const dispatch = useAppDispatch();
  const selectedTaskType = useAppSelector(
    selectSettingsOverviewSelectedTaskType,
  );
  const searchText = useAppSelector(selectSettingsOverviewSearchText);
  const setSearchText = useSetTaskSearchText();

  const {data: tasksFromApi, status, error} = useGetTasksQuery();

  // Delay polling to allow recently locked locks to be released before first load.
  const [startPolling, setStartPolling] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setStartPolling(true);
    }, LOCKS_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const {data: allLocks} = useGetLocksQuery(undefined, {
    skip: !startPolling,
    pollingInterval: LOCKS_RELOAD_MS,
  });

  const {data: users} = useGetUsersQuery();

  const tasksWithLocks = useMemo((): OverviewTask[] | undefined => {
    const allTasks = mapTasksToOverviewTasks(tasksFromApi || [], localization);
    return allLocks &&
      allLocks.length > 0 &&
      allTasks &&
      allTasks.length > 0 &&
      users
      ? allTasks.map(task => {
          const lock = allLocks?.find(
            l => l.contextId === task.workId && l.contextType === 'work',
          );
          return lock
            ? {
                ...task,
                lockedBy: lock.lockedBy,
              }
            : task;
        })
      : allTasks;
  }, [localization, allLocks, tasksFromApi, users]);

  const filteredTasks = useSearchedTasks(
    tasksWithLocks,
    selectedTaskType,
    searchText,
  );

  const handleSetSelectedTaskType = useCallback(
    (taskType: TaskTypeAll) => {
      dispatch(overviewSelectedTaskTypeUpdated(taskType));
    },
    [dispatch],
  );

  // Reset pagination when task type changes
  const taskTypeRef = useRef<TaskTypeAll>(selectedTaskType);
  const {resetPaginationModel} = useColumnConfigState('task');
  useEffect(() => {
    if (taskTypeRef.current !== selectedTaskType) {
      taskTypeRef.current = selectedTaskType;
      resetPaginationModel();
    }
  }, [resetPaginationModel, selectedTaskType]);

  return {
    allTasks: tasksWithLocks || [],
    filteredTasks,
    taskTypes: getTaskTypes(selectedTaskType),
    selectedTaskType,
    setSelectedTaskType: handleSetSelectedTaskType,
    searchText,
    setSearchText,
    loading:
      status === QueryStatus.uninitialized || status === QueryStatus.pending,
    errorMsg: error ? 'Failed' : undefined,
  };
};
